import React from 'react';
import { useReward } from 'react-rewards';
import { useGate } from 'statsig-react';
import { ContractMethod } from '@soundxyz/utils/const';
import {
  ERROR_TYPE,
  MINTING_ERROR_ACTIONS,
  PILLARS,
  TRANSACTION_ERROR_ACTIONS,
} from '@soundxyz/utils/constants/logging';
import { theme } from '@components-shared/src/stitches';
import { SamTransactionFailedModal } from '@components/modals/Buy/Sam/SamTransactionFailedModal';
import { SelectEditionModal } from '@components/modals/Comment/SelectEdition';
import { SetTimeSlotModal } from '@components/modals/Comment/SetTimeSlot';
import { CommentModalV2 } from '@components/modals/CommentV2';
import { ToastContext } from '@context/toast';
import { GetDraftDocument, GetReleaseMintDetailsDocument } from '@graphql/reactQuery';
import {
  EarningsPageDocument,
  invalidateOperations,
  ReferralEarningsHookDocument,
  ReleaseEarningsHookDocument,
} from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';
import { FEATURE_GATES } from '@utils/constants';
import { ModalContext, ModalType } from '../modal';
import type { TransactionStatusLineItem } from './schema';
import { ExtendedContractMethod, IndividualTransactionStatus } from './schema';

export function useTransactionStatusUpdate() {
  const { closeModalByType, openModal } = ModalContext.useContainer();
  const { openToast } = ToastContext.useContainer();
  const logError = useLogError();
  const { value: commentV2Enabled } = useGate(FEATURE_GATES.COMMENT_MODAL_V2);

  const { reward } = useReward('withdrawReward', 'emoji', {
    elementCount: 50,
    emoji: ['💸', '🫰', '💰'],
    zIndex: parseInt(theme.zIndices.above3.value, 10),
    lifetime: 500,
  });

  function refetchEarningsInfo() {
    invalidateOperations({
      operations: [ReleaseEarningsHookDocument, ReferralEarningsHookDocument, EarningsPageDocument],
    });
  }

  function onTransactionStatusUpdate({
    lineItem,
    quantity = 1,
  }: {
    lineItem: TransactionStatusLineItem;
    quantity?: number;
  }) {
    switch (lineItem.transaction.contractMethod) {
      case ContractMethod.SAM__BUY_EDITION:
        switch (lineItem.status) {
          case IndividualTransactionStatus.SUCCESS: {
            openToast({
              text: 'Song purchased! You can now leave a comment on the song.',
              variant: 'success',
            });
            if (!lineItem.transaction.releaseId) {
              const message = `Missing releaseId in buy edition message, txHash=${lineItem.transaction.hash}`;
              logError({
                action: MINTING_ERROR_ACTIONS.EDITION_MINT_ERROR,
                error: new Error(message),
                errorType: ERROR_TYPE.MISSING_RELEASE,
                message,
                closeProcessingModal: true,
                isArtistOnlyPage: false,
                pillar: PILLARS.MINTING,
                indexedTags: {
                  txHash: lineItem.transaction.hash,
                },
                level: 'error',
              });
            } else {
              if (commentV2Enabled) {
                openModal(ModalType.COMMENT_MODAL_V2, {
                  body: (
                    <CommentModalV2
                      releaseId={lineItem.transaction.releaseId}
                      preselectEdition={quantity === 1}
                    />
                  ),
                });
              } else {
                if (quantity > 1) {
                  openModal(ModalType.SELECT_EDITION, {
                    body: <SelectEditionModal releaseId={lineItem.transaction.releaseId} />,
                  });
                } else {
                  openModal(ModalType.SET_TIMESLOT, {
                    body: (
                      <SetTimeSlotModal
                        releaseId={lineItem.transaction.releaseId}
                        type="purchased"
                      />
                    ),
                  });
                }
              }
            }
            break;
          }
          case IndividualTransactionStatus.FAILED: {
            openModal(ModalType.SAM_TRANSACTION_FAILED, {
              body: <SamTransactionFailedModal transactionType="Buy" />,
            });
            break;
          }
          case IndividualTransactionStatus.UNKNOWN: {
            break;
          }
          default: {
            break;
          }
        }
        break;
      case ContractMethod.ARTIST__BUY_EDITION: {
        if (lineItem.status === IndividualTransactionStatus.SUCCESS) {
          openToast({
            text: 'Song purchased! You can now leave a comment on the song.',
            variant: 'success',
          });
          if (!lineItem.transaction.releaseId) {
            const message = `Missing releaseId in buy edition message`;
            logError({
              action: MINTING_ERROR_ACTIONS.EDITION_MINT_ERROR,
              error: new Error(message),
              errorType: ERROR_TYPE.MISSING_RELEASE,
              message,
              closeProcessingModal: true,
              isArtistOnlyPage: false,
              pillar: PILLARS.MINTING,
              indexedTags: {
                txHash: lineItem.transaction.hash,
              },
              level: 'error',
            });
          } else {
            if (commentV2Enabled) {
              openModal(ModalType.COMMENT_MODAL_V2, {
                body: (
                  <CommentModalV2
                    releaseId={lineItem.transaction.releaseId}
                    preselectEdition={quantity === 1}
                  />
                ),
              });
            } else {
              if (quantity > 1) {
                openModal(ModalType.SELECT_EDITION, {
                  body: <SelectEditionModal releaseId={lineItem.transaction.releaseId} />,
                });
              } else {
                openModal(ModalType.SET_TIMESLOT, {
                  body: (
                    <SetTimeSlotModal releaseId={lineItem.transaction.releaseId} type="purchased" />
                  ),
                });
              }
            }
          }
        } else {
          openToast({
            text: 'Transaction failed',
            variant: 'error',
          });
        }
        break;
      }
      case ContractMethod.DRAFT_SPLIT_MAIN__CREATE_SPLIT: {
        if (lineItem.status === IndividualTransactionStatus.SUCCESS) {
          openToast({
            text: 'Split creation completed!',
            variant: 'success',
          });
        } else {
          openToast({
            text: 'Transaction failed',
            variant: 'error',
          });
        }
        closeModalByType(ModalType.PROCESSING);
        break;
      }
      case ContractMethod.SPLIT_MAIN__DISTRIBUTE_ETH: {
        if (lineItem.status === IndividualTransactionStatus.SUCCESS) {
          openToast({
            text: 'Split distribution completed!',
            variant: 'success',
          });
        } else {
          openToast({
            text: 'Transaction failed',
            variant: 'error',
          });
        }
        closeModalByType(ModalType.PROCESSING);
        break;
      }

      case ContractMethod.SAM__SELL_EDITION: {
        switch (lineItem.status) {
          case IndividualTransactionStatus.SUCCESS: {
            reward();
            openToast({
              text: 'Your funds were successfully transferred.',
              variant: 'success',
            });
            break;
          }
          case IndividualTransactionStatus.FAILED: {
            openModal(ModalType.SAM_TRANSACTION_FAILED, {
              body: <SamTransactionFailedModal transactionType="Sell" />,
            });
            break;
          }
          case IndividualTransactionStatus.UNKNOWN: {
            break;
          }
          default: {
            closeModalByType(ModalType.PROCESSING);
            break;
          }
        }
        break;
      }
      case ContractMethod.AFFILIATE__WITHDRAW:
      case ContractMethod.MULTICALL__WITHDRAW: {
        if (lineItem.status === IndividualTransactionStatus.SUCCESS) {
          refetchEarningsInfo();
          reward();
          openToast({
            text: 'Your funds were successfully transferred.',
            variant: 'success',
          });
        } else {
          openToast({
            text: 'Transaction failed',
            variant: 'error',
          });
        }
        closeModalByType(ModalType.PROCESSING);
        break;
      }

      case ExtendedContractMethod.ENABLE_AFFILIATE_FEE: {
        if (lineItem.status === IndividualTransactionStatus.SUCCESS) {
          openToast({
            text: 'Curator rewards have been enabled for this release',
            variant: 'success',
          });
        } else {
          openToast({
            text: 'Transaction failed',
            variant: 'error',
          });
        }
        closeModalByType(ModalType.PROCESSING);
        break;
      }
      case ExtendedContractMethod.DISABLE_AFFILIATE_FEE: {
        if (lineItem.status === IndividualTransactionStatus.SUCCESS) {
          openToast({
            text: 'Curator rewards have been disabled for this release',
            variant: 'success',
          });
        } else {
          openToast({
            text: 'Transaction failed',
            variant: 'error',
          });
        }
        closeModalByType(ModalType.PROCESSING);
        break;
      }

      case ContractMethod.SOUND_CREATOR__CREATE_SOUND_AND_MINTS: {
        invalidateOperations({
          operations: [GetReleaseMintDetailsDocument],
        });

        break;
      }
      case ContractMethod.DRAFT_SOUND_CREATOR__CREATE_SOUND_AND_MINTS: {
        invalidateOperations({
          operations: [GetDraftDocument],
        });

        break;
      }
      case ExtendedContractMethod.MULTICALL__EDIT: {
        // TODO: we should handle more cases here?
        break;
      }
      default: {
        openToast({
          text: `Transaction ${lineItem.status}`,
        });
        const message = 'Unhandled transaction notification';
        logError({
          action: TRANSACTION_ERROR_ACTIONS.TRANSACTION_ERROR,
          error: new Error(message),
          errorType: ERROR_TYPE.UNHANDLED_CONTRACT_METHOD,
          message,
          closeProcessingModal: true,
          isArtistOnlyPage: false,
          pillar: PILLARS.TRANSACTION,
          indexedTags: {
            txHash: lineItem.transaction.hash,
            contractMethod: lineItem.transaction.contractMethod,
            status: lineItem.status,
          },
          level: 'warning',
        });
      }
    }
  }

  return {
    onTransactionStatusUpdate,
  };
}
